import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { organizationsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { CircleNotch, Buildings, MapPin, Phone } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export default function OrganizationSettingsPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [savedSnapshot, setSavedSnapshot] = useState({ name: '', address: '', phone: '' });

  const loadOrg = useCallback(async () => {
    try {
      const res = await organizationsApi.list();
      if (res.data.length > 0) {
        const o = res.data[0];
        setOrg(o);
        const snap = {
          name: o.name || '',
          address: o.address || '',
          phone: o.phone || '',
        };
        setName(snap.name);
        setAddress(snap.address);
        setPhone(snap.phone);
        setSavedSnapshot(snap);
      }
    } catch { toast.error('Failed to load organization'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOrg(); }, [loadOrg]);

  const isDirty =
    name.trim() !== (savedSnapshot.name || '').trim() ||
    address.trim() !== (savedSnapshot.address || '').trim() ||
    phone.trim() !== (savedSnapshot.phone || '').trim();

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Organization name is required'); return; }
    if (!org || !isDirty) return;
    setSaving(true);
    try {
      const res = await organizationsApi.update(org.id, {
        name: name.trim(),
        address: address.trim() || null,
        phone: phone.trim() || null,
      });
      setOrg(res.data);
      const snap = {
        name: res.data.name || '',
        address: res.data.address || '',
        phone: res.data.phone || '',
      };
      setName(snap.name);
      setAddress(snap.address);
      setPhone(snap.phone);
      setSavedSnapshot(snap);
      toast.success('Organization updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20"><CircleNotch className="w-8 h-8 animate-spin text-brand-500" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6" data-testid="org-settings-page">
        <PageHeader
          title={t('orgSettings.title')}
        />

        {org ? (
          <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                <Buildings className="w-4 h-4 text-brand-500" /> {t('orgSettings.details')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : ''}>{t('orgSettings.orgName')} *</Label>
                  <Input value={name} onChange={e => setName(e.target.value)}
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="org-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className={`flex items-center gap-1.5 ${isDark ? 'text-zinc-300' : ''}`}><MapPin className="w-3.5 h-3.5" /> {t('orgSettings.address')}</Label>
                  <Input value={address} onChange={e => setAddress(e.target.value)}
                    placeholder="123 Business Street, City"
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="org-address-input" />
                </div>
                <div className="space-y-2">
                  <Label className={`flex items-center gap-1.5 ${isDark ? 'text-zinc-300' : ''}`}><Phone className="w-3.5 h-3.5" /> {t('orgSettings.phone')}</Label>
                  <Input value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="org-phone-input" />
                </div>
                <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  Created: {org.created_at ? formatDate(org.created_at) : 'N/A'}
                  {org.updated_at && ` | Last updated: ${formatDate(org.updated_at)}`}
                </div>
                <Button type="submit" disabled={saving || !isDirty} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="save-org-btn">
                  {saving ? <CircleNotch className="w-4 h-4 animate-spin mr-2" /> : null} {t('common.save')}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className={isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}>
            <CardContent className="py-12 text-center">
              <Buildings className={`w-12 h-12 mx-auto mb-3 opacity-30 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
              <p className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{t('orgSettings.noOrg')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
